import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { TermoFuncionalidadeVinculo } from '../models/termo.model';

/** Mesmo tamanho de página da lista de termos, para a tela paginar de um jeito só. */
const PAGE_SIZE = 8;

/**
 * Painel direito da Gestão de Termos: funcionalidades do portal que consomem o
 * termo selecionado.
 *
 * A marcação é mantida aqui até a pessoa salvar, então dá para marcar várias e
 * confirmar de uma vez — inclusive em páginas diferentes, já que a seleção vive
 * fora da lista exibida. Trocar de termo descarta o que não foi salvo. A busca é
 * local, sobre a lista já recebida.
 */
@Component({
  selector: 'app-termo-vinculos-panel',
  standalone: true,
  imports: [PaginationComponent],
  templateUrl: './termo-vinculos-panel.component.html',
  styleUrl: './termo-vinculos-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TermoVinculosPanelComponent {
  /** Funcionalidades do portal com o vínculo atual do termo. */
  readonly funcionalidades = input.required<readonly TermoFuncionalidadeVinculo[]>();

  /** Emite os ids das funcionalidades marcadas quando a pessoa salva. */
  readonly salvar = output<readonly number[]>();

  readonly busca = signal('');
  readonly currentPage = signal(1);
  readonly pageSize = PAGE_SIZE;

  /** Ids marcados na tela, ainda não salvos. */
  private readonly selecionados = signal<ReadonlySet<number>>(new Set());

  readonly funcionalidadesFiltradas = computed(() => {
    const busca = this.busca().trim().toLowerCase();
    if (!busca) {
      return this.funcionalidades();
    }

    return this.funcionalidades().filter((funcionalidade) =>
      funcionalidade.nome.toLowerCase().includes(busca),
    );
  });

  readonly funcionalidadesPaginadas = computed(() => {
    const inicio = (this.currentPage() - 1) * PAGE_SIZE;
    return this.funcionalidadesFiltradas().slice(inicio, inicio + PAGE_SIZE);
  });

  readonly totalSelecionadas = computed(() => this.selecionados().size);

  /** Conta sobre o catálogo inteiro, e não sobre a página à vista. */
  readonly resumoSelecao = computed(
    () =>
      `${this.totalSelecionadas()} de ${this.funcionalidades().length} funcionalidades selecionadas`,
  );

  constructor() {
    // Recarrega a marcação a cada troca de termo, descartando o que não foi salvo.
    effect(() => {
      const vinculados = this.funcionalidades()
        .filter((funcionalidade) => funcionalidade.vinculado)
        .map((funcionalidade) => funcionalidade.id);

      this.selecionados.set(new Set(vinculados));
      this.currentPage.set(1);
    });
  }

  isSelecionada(id: number): boolean {
    return this.selecionados().has(id);
  }

  onToggle(id: number): void {
    this.selecionados.update((atual) => {
      const proximo = new Set(atual);

      if (proximo.has(id)) {
        proximo.delete(id);
      } else {
        proximo.add(id);
      }

      return proximo;
    });
  }

  onBuscaChange(valor: string): void {
    this.busca.set(valor);
    // Buscar recomeça da primeira página: o resultado é outro, e a página em que
    // a pessoa estava não corresponde a nada nele.
    this.currentPage.set(1);
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
  }

  onSalvar(): void {
    this.salvar.emit([...this.selecionados()]);
  }
}
